/**
 * @file       transport/socketio.js
 * @author     Ryan Rossiter, ryan@kingsds.network
 * @author     Wes Garland, wes@kingsds.network
 * @date       January 2020, March 2022
 *
 * This module implements the SocketIO Transport that is
 * used by the protocol to connect and communicate with peers using
 * SocketIO connections.
 *
 * Transport can operate in either ack mode or non-ack-mode, depending on the value of this.ackMode.
 * Ack mode sends an extra socketio-layer packet back after each message is received, and theoretically
 * this can be use to meter or measure bandwidth or potentially second-guess socketio's ability to
 * interleave its heartbeat messages OOB from the main traffic.  This mode could potentially be enabled
 * on-demand as well, although it appears that it is not necessary to get the desired behaviour at this
 * point, so the feature is off-by-default (although reasonably well-tested).
 *
 * ** Queues **
 * The rxPending message queue is a list of messages which have been received from the remote, but which
 * we are not yet ready to emit to the Connection layer. This can happen, in theory, when multiple
 * concatenated messages get interleaved; rxPending imposes an ordering here.
 *
 * The rxFragment message queue is a list of messages which have been received from the remove, but which
 * are part of a fragmented message. The rxPending queue hold an array to reference these fragments. Once
 * of the messages fragments have been received, they are joined locally into a string, which makes that
 * message ready for emission from rxPending.
 *
 * The txPending message queue is a queue that holds messages which we will send over the wire. This
 * queue allows us to receive messages from the Connection layer faster than they can be delivered to
 * the network buffer in socketio/the kernel/the network.
 */
'use strict';

const dcpEnv         = require('dcp/common/dcp-env');
const chalk          = require('dcp/utils').chalk();
const bearer         = require('./http-bearer');

const { EventEmitter }     = require('events');
const { leafMerge }        = require('dcp/utils/obj-merge');
const { DcpURL }           = require('dcp/common/dcp-url');
const { DCPError }         = require('dcp/common/dcp-error');
const { setImmediateN }    = require('dcp/common/dcp-timers');
const { setImmediate }     = require('dcp/common/dcp-timers');
const { requireNative }    = require('dcp/dcp-client/webpack-native-bridge');
const { assert }           = require('dcp/common/dcp-assert');

const dcpBuild       = require('dcp/build');
const debugging      = require('dcp/debugging').scope('dcp');
const debugBuild     = require('dcp/build').config.build === 'debug';
const usingDebugger  = dcpEnv.platform === 'nodejs' && requireNative('module')._cache.niim instanceof requireNative('module').Module;
const process        = dcpEnv.platform === 'nodejs' ? requireNative('process') : require('process');
const inspect        = dcpEnv.platform === 'nodejs' ? requireNative('util').inspect.custom : Symbol('inspect');

var   listenerSeq    = 0;
var   initiatorSeq   = 0;

if (debugging('socketio-lib'))
{
  if (process.env.DEBUG)
    process.env.DEBUG += ',engine';
  else
    process.env.DEBUG = 'engine';

  let debugFlags = 'socket.io-client:socket,socket.io:client,socket.io:server';
  if (debugging('verbose') && !debugging('all'))
    debugFlags += ',socket.io*,engine.io*';

  if (debugging('socketio-lib'))
  {
    /* DCP_DEBUG=dcp:socketio,dcp:verbose to see everything */
    process.env.DEBUG += ',';
    if (debugging('verbose') && !debugging('all'))
      process.env.DEBUG += debugFlags
  }
}

class SocketIOTransport extends EventEmitter
{
  /**
   * @constructor
   * @param {socket} newSocket            Target: an instance of socket.io Socket that represents
   *                                      a new connection.
   *                                      Initiator: unused
   */
  /**
   * Create new instance of a socketio-flavoured Transport.
   *
   * @param {object}  config              dcpConfig fragment
   *                                      - Initiator: has .location and optionally .friendLocation
   *                                      - Target:    has .listen and .location
   *
   * @param {object}  options             The `socketio` property of a protocol connectionOptions
   *                                      object, which was built from a combination of defaults and
   *                                      various dcpConfig components.
   *
   * @returns instance suitable for target or initiator, depending on mode, but does not manage connections.
   * @constructor
   */
  constructor(config, options={}, newSocket)
  {
    super('Protocol SocketIO Transport');
    this.on('error', (error) => { debugging() && console.debug(this.debugLabel, 'event:', error)}); /* side-effect; avoid emit->throw lunacy */

    const that = this;
    assert(DcpURL.isURL(config.location));
    assert(typeof options === 'object');

    this.name          = 'socketio';
    this.url           = config.location;
    this.msgSequence   = 0;
    this.isClosed      = false;  /* true => connection finalization begun */
    this.isFinal       = false;  /* true => connection finalization completed */
    this.hasConnected  = false;  /* true => one 'connect' event has been processed */
    this.rxFragments   = {};     /* wire-level messages we have received which are really message fragments */
    this.rxPending     = [];     /* strings which are messages that are complete and ready to emit, or objects that in this.rxFragments */
    this.txPending     = [];     /* wire-level messages we have prepared for transmission and will transmit asap */
    this.txNumInFlight = 0;      /* number of txPending sent but not acknowleged */
    this.txMaxInFlight = 2;      /* max allowed for txNumInFlight; less than 2 causes bad perf when ackMode /wg apr 2022 */
    this.options       = this.buildOptions(options);
    this.mode = {
      ack:    Boolean(this.options.ackMode),   /* true => we require message acknowlegements */
    };

    if (dcpEnv.platform === 'nodejs')
    {
      this[inspect] = () => `[Object SocketIOTransport(${(this.isClosed && 'closed') || 'active'}) `
                          + `${this.url.pathname} ${this.networkAddress}]`;
    }

    /* Peer property inherits from remote during announcement - values are untrusted!
     * This definition defines the absolute minimum capabilities of any supported client or server. We
     * only exceed these capabilities once the peer has announced that they can.
     */
    this.peer = {
      canOoo:                   false,  /* true => peer can handle out-of-order messages */
      canAck:                   false,  /* true => peer supports ack mode */
      wantsAck:                 false,  /* true => we send acks to peer */
      wantsConcat:              true,   /* true => peer can handle multipart messages */
      rxMaxFragmentSize:        1e6,    /* maximum size in JS characters of any given message fragment */
      rxMaxFragmentCount:       100,    /* maximum number of fragments per message */
    };

    if (!newSocket) /* Initiator */
    {
      const socketIO = options.module
            || (require('dcp/common/dcp-env').platform !== 'nodejs'
                ? require('socket.io-client')
                : requireNative('socket.io-client'));

      this.debugLabel = `socketio(i:${++initiatorSeq}):`;
      this.rxReady = true; /* relax, said the night man */

      bearer.a$isFriendlyUrl(config.friendLocation).then((useFriendLocation) => {
        const connectUrl = useFriendLocation ? config.friendLocation : config.location;
        debugging('socketio') && console.debug(this.debugLabel, 'connecting to', connectUrl.href);
        debugging('socketio') || debugging('socketio-config') && dumpOptions(this.debugLabel, this.options);

        this.socket = socketIO.connect(connectUrl.origin, this.options);
        this.socket.on('connect_error', (error) => !this.isClosed && this.handleConnectErrorEvent(error));
        this.socket.on('connect',       ()      => !this.isClosed && this.handleConnectEvent());

        useSocket();
      }).catch((error) => {
        debugging() && console.error(this.debugLabel, error);
        this.emit('error', error);
      });
    }
    else /* Target - receives socketio instance from SocketIOListener.handleConnectionEvent */
    {
      if (dcpEnv.platform !== 'nodejs')
        throw new Error('socketio: target mode only supported in nodejs');

      this.debugLabel = 'socketio(t:<new>):';
      this.socket = newSocket;
      debugging('socketio') && console.debug(this.debugLabel, `new socket from ${this.networkAddress}`
                                             + ` on ${this.socket.handshake && this.socket.handshake.url}, ${this.socket.id}`);
      useSocket();
    }

    function useSocket()
    {
      if (that.isClosed)
        return;

      that.socket.compress(require('dcp/build').config !== 'debug'); /* try to keep traffic sniffable in debug */
      that.socket.on('message',    (msg)    => !that.isFinal  && that.handleMessageEvent(msg));
      that.socket.on('disconnect', (reason) => !that.isClosed && that.handleDisconnectEvent(reason));
      that.announce();
    }
  }

  get networkAddress()
  {
    const address = this.socket?.handshake?.address || this.socket?.io?.engine?.hostname;
    return address ? address.replace(/^::ffff:/,'') : '<no peer ip>';
  }

  /**
   * API - determine if a transport is usable or not.
   * @returns true if the transport is not closed.
   */
  ready()
  {
    return !this.isClosed;
  }

  /**
   * Handle the socket.io disconnect event, which is fired upon disconnection.
   * For some reasons (explicit disconnection), the socket.io code will not try to reconnect, but
   * in all other cases, the socket.io client will normally wait for a small random delay and then
   * try to reconnect, but in our case, we simply tear the transport down and let the Connection
   * class handle reconnects, since it might prefer a different transport at this point anyhow.
   *
   * One tricky case here is ping timeout, since the ping timeout includes the time to transmit the
   * packet before it was sent.
   *
   * @param {string}    reason          Possible reasons for disconnection.
   */
  handleDisconnectEvent(reason)
  {
    debugging('socketio') && console.debug(this.debugLabel, `disconnected from ${this.networkAddress}; reason=${reason}`);

    if (this.isClosed !== true)
      setImmediate(() => this.#close(reason));

    switch(reason)
    {
      case 'io client disconnect':
        return; /* we called socket.disconnect() from another "thread" */
      case 'io server disconnect':
      case 'ping timeout':
      case 'transport close':
      case 'transport error':
        this.emit('end', reason);
        /* fallthrough */
      default:
    }
  }

  /**
   * Handle a socketio message from the peer.
   *
   * Most of the time, a socketio message contains a DCP message, however we support other message
   * types as well, with a mechanism inspired by 3GPP TS 23.040 (GSM 03.40) message concatenation.
   */
  handleMessageEvent(msg)
  {
    if (typeof msg !== 'string')
    {
      debugging('socketio') && console.debug(this.debugLabel, `received unsupported ${typeof msg} message from peer; ignored`, this.networkAddress);
      return;
    }

    try
    {
      switch (msg[0])
      {
        case '{': /* all DCP Messages are JSON */
          this.sendAck();
          this.processMessage(msg);
          break;
        case 'E':
          this.processExtendedMessage(msg);
          this.sendAck(); /* must be after announce processing */
          break;
        case 'A':
          this.processAcknowledgement(msg);
          break;
        case 'T':
          debugging('socketio') && console.debug(this.debugLabel, 'Received debug message:', msg);
          break;
        default:
          /* adhoc messages not supported in this transport */
          throw new DCPError(this.debugLabel, `Unrecognized message type indicator from ${this.networkAddress} (${msg.charCodeAt(0)})`, 'DCPC-1102');
      }
    }
    catch(error)
    {
      debugging('socketio') && console.debug(this.debugLabel, 'handleMessageEvent: transport error, closing\n ', error);
      this.emit('error', error);
      setImmediate(() => this.#close(error.message));
    }
  }

  /**
   * Process an ordinary message. This is just a hunk of JSON that gets passed up to the connection.
   * @param {string} msg
   */
  processMessage(msg)
  {
    this.rxPending.push(msg);
    this.emitReadyMessages();
  }

  /**
   * Remote has acknowledged the receipt of a message fragment. When in ack mode, this
   * triggers us to send more messages from the txPending queue. We always ack event when
   * not in ack-mode. See processExtendedMessage() comment for header description.
   *
   * Future: this is where maxInFlight might be tuned, based on how fast we get here
   *
   * @param {string} _msg
   */
  processAcknowledgement(_msg)
  {
    if (this.txNumInFlight > 0)
      this.txNumInFlight--;
    else
    {
      if (this.options.strict && !this.mode.ack)
        throw new DCPError(this.debugLabel, `Synchronization error: received unexpected acknowledgement message from ${this.networkAddress}`, 'DCPC-1109');
    }

    this.drainTxPending_soon();
  }

  /**
   * Extended messages have the following format:
   * Octet(s)           Description
   * 0                  E (69) => generic ext msg,
   *                    A (65) => ack,
   * 1..4               Header size in hexadecimal
   * 5                  Header Type
   *                    C (67) => concatenation
   *                    O (79) => out of order
   *                    P (80) => peer capability announcement
   * 6..6 + N:          Header
   *
   * Upon receipt, extended messages are acknowledged by retransmitting their header, but
   * with the first character changed from E to A.
   *
   * @param {string} msg
   */
  processExtendedMessage(msg)
  {
    const headerSize = parseInt(msg.slice(1,5), 16);
    const headerType = msg[5];
    const header     = msg.slice(6, 6 + headerSize);
    const payload    = msg.slice(6 + headerSize);

    switch(headerType)
    {
      case 'C':
        this.processExtendedMessage_concatenated (header, payload);
        break;
      case 'O':
        this.processExtendedMessage_outOfOrder   (header, payload);
        break;
      case 'P':
        this.processExtendedMessage_announcement (header, payload);
        break;
      default:
        debugging() && console.debug(this.debugLabel, `ignoring unrecognized extended message header type indicator (${headerType.charCodeAt(0)})`);
        if (this.options.strict)
          throw new DCPError(`Unrecognized extended message header type indicator (${headerType.charCodeAt(0)})`, 'DCPC-1101');
    }
  }

  /**
   * Process an out-of-order message. This is the same as basically the same as processMessage(), but
   * we don't care about message ordering.
   */
  processExtendedMessage_outOfOrder(header, payload)
  {
    this.rxPending.unshift(payload);
    this.emitReadyMessages();
  }

  /**
   * Process the peer's capability announcement message.
   */
  processExtendedMessage_announcement(header, payload)
  {
    const peer = JSON.parse(payload);

    if (this.peer.hasAnnounced)
      debugging() && console.debug(this.debugLabel, '*** received unexpected peer capability announcement:', peer);
    else
      debugging('socketio') && console.debug(this.debugLabel, 'received peer capability announcement', peer);

    Object.assign(this.peer, peer);
    debugging('socketio') || debugging('socketio-config') && console.debug(this.debugLabel, 'peer configuration:');
    debugging('socketio') || debugging('socketio-config') && prettyPrintOptions(this.peer);
  }

  /**
   * We have received a fragment of a concatenation extended message. Memoize the fragment, and
   * if this is the last fragment, transform the fragments list into a pending message string and
   * then emits all ready messages (pending message strings).
   *
   * This code is capable of handling messages whose fragments arrive out of order. Complete messages
   * are emitted in the order that their first parts are received. Socketio is supposed to be total
   * order-aware but I wanted a safety belt for intermingled messages without occupying the entire
   * socketio network buffer on the first pass of the event loop. Because the first message fragment
   * is emitted to socketio on the same event loop pass as their Connection::send calls, I believe
   * that are no cases where messages will be emitted out of order at the other end, even when
   * intermingled on the wire, and the Nth message is much longer than any (N+k)th messages.
   *
   * @param {object} header     the extended message header
   * @param {string} payload    the payload of the extended message, i.e. a message fragment
   */
  processExtendedMessage_concatenated(header, payload)
  {
    header = JSON.parse(header);

    if (!(header.seq < header.total))
      throw new DCPError(`corrupt header for part ${header.seq + 1} of ${header.total} of message ${header.msgId}`, 'DCPC-1108');

    if (this.options.maxFragmentCount && header.total > this.options.maxFragmentCount)
      throw new DCPError('message fragment count limit exceeded', 'DCPC-1113');

    if (payload.length > this.options.maxFragmentSize)
      throw new DCPError('message fragment size limit exceeded', 'DCPC-1114');

    /* First fragment received, initial data structures and memoize the message's arrival order via pending list */
    if (!this.rxFragments[header.msgId])
    {
      const fragments = [];
      this.rxFragments[header.msgId] = fragments;
      this.rxPending.push(fragments);
    }

    this.rxFragments[header.msgId][header.seq] = payload;
    debugging('socketio') && console.debug(this.debugLabel, 'received part', header.seq + 1, 'of', header.total, 'for message', header.msgId);

    if (header.total !== this.rxFragments[header.msgId].filter(el => el !== undefined).length)
      return;

    debugging('socketio') && console.debug(this.debugLabel, 'received all parts of message', header.msgId);

    const idx = this.rxPending.indexOf(this.rxFragments[header.msgId])
    this.rxPending[idx] = this.rxFragments[header.msgId].join('');
    delete this.rxFragments[header.msgId];
    this.emitReadyMessages();
  }

  /**
   * Maybe send a transport-layer ack message to acknowledge the receipt of a transport-layer message.
   * This routine understands he peer's capabilities, and if the peer doesn't want the ack message, we
   * just don't sent it here.
   */
  sendAck(message)
  {
    if (!this.peer.wantsAck)
      return;

    debugging('socketio') && console.debug(`${this.debugLabel} sending transport ack`);
    this.socket.send('A0000-ACK'); /* Ack w/o header = normal message */
  }

  /**
   * Emit message events so that the connection can process them. The pending property is an
   * array which is used to order messages, so that they are emitted in the order they were
   * sent. There are edge cases in the concatenation code where a sender could theoretically
   * interleave multiple messages, and have a second, shorter, message fully received before
   * the first is fully transmitted.
   *
   * The pending property stores only strings or objects; objects are effectively positional
   * memoes which are turned into strings when they are ready to be emitted to the connection
   * for processing.
   */
  emitReadyMessages()
  {
    while (this.rxReady && this.rxPending.length && typeof this.rxPending[0] === 'string' && this.listenerCount('message') > 0)
      this.emit('message', this.rxPending.shift());
  }

  /**
   * Close the current instance of Socket.
   *
   * This function effectively finalizes the socket so that it can't be used any more and
   * (hopefully) does not entrain garbage. A 'close' event is emitted at the end of socket
   * finalization and should be hooked by things that might entrain this transport instance to free
   * up memory, like maybe a list of active transports, etc.
   *
   * We add few extra hops on/off the event loop here to try and clean up any messages halfway in/out
   * the door that we might want to process. That happens between the setting of the isClosed and
   * isFinal flags.
   *
   * @param {string}  reason
   */
  #close(reason)
  {
    const that = this;
    debugging('socketio') && console.debug(this.debugLabel, 'closing connection' + (reason ? ` (${reason})` : ''));

    function finalize(socket)
    {
      if (that.isFinal)
        return;
      that.isFinal = true;

      if (socket)
        socket.removeAllListeners();

      /* Free up memory that might be huge in case something entrains us */
      that.rxPending.length = 0;
      that.txPending.length = 0;
      for (let msgId in that.rxFragments)
        that.rxFragments[msgId].length = 0;
    }

    function closeSocket()
    {
      const socket = that.socket;

      try
      {
        if (socket)
          socket.disconnect();
        that.isClosed = true;
        that.emit('close', reason);
        delete that.socket;
      }
      finally
      {
        finalize(socket);
      }
    }

    if (!this.isClosed)
    {
      /* If we called close, drain any pending messages and then disconnect */
      if (reason === 'api-call')
      {
        this.drainTxPending();
        setImmediate(()=>closeSocket());
      }
      else
        closeSocket();
    }
  }

  /* API - close the transport, free up memory. */
  close()
  {
    assert(arguments.length === 0)
    this.#close('api-call');
  }

  /**
   * Handle the socketio connect_error event, which is fired when:
   * - the low-level connection cannot be established
   * - the connection is denied by the server in a middleware function
   *
   * In the first case, socket.io will automatically try to reconnect, after a delay,
   * except that we will cancel that reconnection attempt here by killing the transport
   * instance so that the Connection class can try failing over to an alternate transport.
   *
   * @param {Error}     error   optional instance of error describing the underlying reason for the
   *                            connect failure.
   *                            - If the underlying reason is expressible by an HTTP status code,
   *                              this code will be placed as a Number in the httpStatus property.
   *                            - If the httpStatus property is set, best efforts are made to put
   *                              the HTTP headers into a { key, value } object as the httpHeaders
   *                              property.
   */
  handleConnectErrorEvent(error)
  {
    debugging('socketio') && console.debug(this.debugLabel, `unable to connect to ${this.url}`,
                                           error instanceof Error ? error.message : `due to ${error}`);
    error.transport = this.name;
    if (error.code)
      error.socketioCode = error.code;

    if (error.type === 'TransportError' && typeof error.description === 'number')
    {
      /* HTTP-Bearer-level errors handled here */
      error.httpStatus = Number(error.description);
      if (error.context && error.context.getAllResponseHeaders)
        error.httpHeaders = Object.fromEntries(
          error.context.getAllResponseHeaders()
            .replace(/\r\n/g, '\n').split('\n')          /* Make header section string into an array of lines */
            .map(line => line.match(/(^[^:]+)(: )(.*)/)) /* Extract each header name and value */
            .filter(a => a)                              /* Eliminate lines which didn't match regex */
            .map(a => [a[1], a[3]])                      /* Turn each into [key, value] for Object.fromEntries */
        );
      if (!error.httpHeaders)
        error.httpHeaders = {};

      error.__statusTextCode = error?.context?.statusText?.code; /* debug only */

      /* Server recycling? */
      if (error?.context?.statusText?.code === 'ECONNREFUSED')
      {
        /* This happens when the remote end daemon isn't listening - eg web server recycle */
        error.code = 'DCP_TRANSPORT_FAILURE_TRANSIENT';
      }
    }
    else if (error.socketioCode === 'parser error' && error.httpStatus === 200)
    {
      /* This happens when the remote end answers with something that isn't even socketio; treat as a
       * permanent failure [never try socketio again] unless it's a 502 page, which can happen during
       * server recycle.
       */
      error.code = 'DCP_TRANSPORT_FAILURE_PERMANENT';
    }

    if (!(error.hasOwnProperty('code')))
    {
      switch(error?.message)
      {
        default:
          error.code = bearer.interpretHttpStatusCode(this.url, error, this.debugLabel);
          if (error.code)
            break;
          debugging() && console.warn(this.debugLabel, 'warning - could not determine transport failure error code, treating as transient failure');
          break;
        case 'timeout' :        /* This happens when the remote end isn't sending TCP ACKs to SYNs */
        case 'websocket error': /* Remote end shutdown connection just as we connected */
          error.code = 'DCP_TRANSPORT_FAILURE_TRANSIENT';
          break;
      }
    }

    this.emit('connect-failed', error);
    this.#close(error.message);
  }

  /**
   * Handle the socketio connect event, which is fired by the Socket instance upon initiator
   * connection and reconnection to a target.
   */
  handleConnectEvent()
  {
    if (this.hasConnected === true)
    {
      /* this transport is not supposed to reuse connections, and we call .close() on disconnect
       * to prevent this. However, the API does not guarantee that will be race-free.
       */
      debugging('socketio') && console.debug(this.debugLabel, `*** reconnected to ${this.networkAddress} (ignoring)`);
      return;
    }

    /* initial connection */
    this.hasConnected = true;
    debugging('socketio') && console.debug(this.debugLabel, 'connected to', this.networkAddress);

    this.drainTxPending();
    setImmediate(() => this.emit('connect')); /* delay slightly to improve chance announce message goes out first */
  }

  /**
   * Send a capability / preference announcement to our peer.
   */
  announce()
  {
    /* This object overrides properties of the remote's `this.peer` object shortly after connection. It
     * is theoretically possible to send multiple announcements as things change from a tuning POV; data
     * provided here should be enough for the peer to know if it needs to request smaller fragments etc.
     */
    const me = {
      canOoo:             true,
      canAck:             true,
      wantsConcat:        true,
      wantsAck:           this.mode.ack,
      rxMaxFragmentSize:  this.options.maxFragmentSize,
      rxMaxFragmentCount: this.options.maxFragmentCount,
      pingInterval:       this.options.pingInterval,
      hasAnnounced:       true,
    };

    debugging('socketio') && console.debug(`${this.debugLabel} sending transport announcement (ack: ${me.wantsAck} sz: ${me.rxMaxFragmentSize} pi: ${me.pingInterval})`);
    this.send('E0000P' + JSON.stringify(me));
    this.drainTxPending();
  }

  /**
   * API: Send a message to the transport instance on the other side of this connection.
   *
   * This function should be treated as infallible, but it does not guarantee the delivery of the
   * message. In the few cases where this function can throw, those cases could not be recovered from
   * with the current state of the surrounding ecosystem. In some distant future, it might be possible
   * to ban the current transport for the current session as we close the transport, triggering the
   * transport.on('end') handler in the DCP Connection code, which could then retry to resume the
   * session with a different transport.
   *
   * @param {string} message    the message to send
   */
  send(message)
  {
    const msgSequence = ++this.msgSequence;

    debugging('socketio') && console.debug(this.debugLabel, `sending message ${msgSequence} to`, this.networkAddress);
    debugging('socketio') && debugging('verbose') && !debugging('all') && console.debug(this.debugLabel, message);
    assert(message);

    /* It's possible to reach this point before the peer's announcement has been made. That's okay, the
     * message will get delivered, however it may not be using optimal concatenation sizes etc.
     */
    if (false
        || !this.socket
        || !(message.length > this.peer.rxMaxFragmentSize)
        || !this.peer.wantsConcat)
      this.txPending.push({ message, msgSequence });
    else
    { /* This is a large message. Use message concatenation to send in fragments. Non-delivery of any
       * fragment is a failure of the entire message and will be handled at the connection layer.
       */
      let total = Math.ceil(message.length / this.peer.rxMaxFragmentSize);

      if (this.peer.rxMaxFragmentCount && total > this.peer.rxMaxFragmentCount)
      {
        this.#close('too many fragments');
        throw new DCPError('message requires more fragments than the peer will allow', 'DCP-1112');
      }

      for (let seq=0; seq < total; seq++)
      {
        let start      = seq * this.peer.rxMaxFragmentSize;
        let fragment   = message.slice(start, start + this.peer.rxMaxFragmentSize);
        let header     = JSON.stringify({ msgId: msgSequence, seq, total });
        let extMessage = 'E' + header.length.toString(16).padStart(4, '0') + 'C' + header + fragment;

        this.txPending.push({ message: extMessage, msgSequence, seq, total });

        /* This should be impossible, but it also effectively kills the connection. */
        if (header.length > 0xFFFF)
        {
          this.#close('header length is 0x' + Number(header.length).toString(16));
          throw new DCPError('extended message header too long', 'DCPC-1104');
        }
      }
    }

    this.drainTxPending_soon();
  }

  /**
   * Send a message as soon as possible, even if that means jumping queue on other messages or
   * message fragments. If the remote does not support OOO messages, we revert to in-order
   * transmission.
   *
   * @note this implementation does not support message-size splitting, etc, like the full send()
   *       code does; that is because OOO is only used in DCP for close messages which are miniscule.
   *
   * @param {string} message
   */
  sendOutOfOrder(message)
  {
    const msgSequence = ++this.msgSequence;
    const oooMessage = 'E0000O' + message;

    debugging('socketio') && console.debug(this.debugLabel, `sending message OOO ${msgSequence} to`, this.networkAddress);
    debugging('socketio') && debugging('verbose') && !debugging('all') && console.debug(this.debugLabel, message);

    if (this.peer.canOoo)
      this.txPending.unshift({ message: oooMessage, msgSequence });
    else
      this.txPending.push({ message: message, msgSequence });

    this.drainTxPending();
  }

  drainTxPending_soon()
  {
    if (this.drainingSoon)
      return;
    this.drainingSoon = true;

    setImmediate(() => this.drainTxPending());
  }

  /**
   * Drain messages from the txPending queue out to socketio's network buffer. We throw away traffic
   * when it can't be sent; this is no different from packets already on the wire when the other end
   * has changed IP number, etc.
   *
   * In ack mode, this drain is controlled by acknowledgement messages so that socketio can have the
   * opportunity to insert ping/pong messages in the data stream while sending a message large enough
   * to need to be sent via concatenation. [ NOTE - it appears to send ping/pong OOB in version 4.4,
   * so currently ack mode is off by default / wg Mar 2022 ]
   *
   * The reason we put non-concat messages through here is to avoid special casing a bunch of nasty
   * things for the sake of a little tiny bit of performance. Sending message<-ACK and queueing through
   * the drain code is necessary if we wind up interleaving multiple message sends in some interesting
   * edge cases; notably, fast-teardown, but a future Connection class that ran without nonces (or multiple
   * nonces) would need this, too.
   *
   * In non-ack mode, the drain is mostly uncontrolled, but we throw each message on a separate pass of
   * the event loop, to try and keep huge message blasts one connection from overwhelming a busy server,
   * as well allowing socketio the chance to send its heartbeat messages and make it less likely that it
   * will hit its http buffer size limit, because that causes an immediate disconnect.
   *
   * Non-ack-mode is compatible with previous dcp5 versions and probably a little faster.
   */
  drainTxPending()
  {
    const that = this;

    if (this.txPending.length)
      debugging('socketio') && console.debug(this.debugLabel, `drain tx pending queue, ${this.txPending.length} ready`
                                             + (this.mode.ack ? `; ${this.txMaxInFlight - this.txNumInFlight} slots available` : ''));
    delete this.drainingSoon;

    if (this.isClosed)
      return;

    for (let i=0;
         !this.isClosed && this.txPending.length && (this.txNumInFlight < this.txMaxInFlight || !this.mode.ack);
         i++)
    {
      const pendingElement = this.txPending.shift();

      if (i === 0)
        writeToSocket(pendingElement);
      else
        setImmediateN(() => writeToSocket(pendingElement), i);

      if (this.mode.ack)
        this.txNumInFlight++;

      if (this.txPending.length === 0) /* just sent the last message */
        this.emit('drain');
    }

    if (this.txPending.length)
      this.drainTxPending_soon();

    function writeToSocket(pendingElement)
    {
      if (that.isClosed)
        return;

      const { message, msgSequence, seq, total } = pendingElement;
      that.socket.send(message);

      if (typeof seq !== 'undefined')
        debugging('socketio') && console.debug(that.debugLabel, `sent fragment ${seq + 1}/${total} of message ${msgSequence} (${message.length} bytes)`);
      else
        debugging('socketio') && console.debug(that.debugLabel, `sent message ${msgSequence} (${message.length} bytes)`);
    }
  }
}
SocketIOTransport.prototype.buildOptions = buildOptions;
SocketIOTransport.prototype.reliable = true;

/**
 * Build the socketio options object, based on the passed-in options, dcp debug state, and internal
 * object state. Method shared between SocketIOTransport and SocketIOListener.
 *
 * Note that the *transport* and *socket.io* both use this object directly. This means that we have to
 * ensure that no transport-level option names conflict with socket.io; it also implies that transport
 * options are namespaced on a per-transport basis; we can never decide to just send a common object
 * into all transports. Common behavioural attributes need to be handled at the Connection level.
 *
 * Baked-in defaults are based on the assumption that the minimum network speed we have connecting to us
 * is about 1.5Mbps. Slower connections can request a smaller fragment size during peer announcement. It
 * would be nice to calculate this automatically someday.
 */
function buildOptions(options)
{
  options = leafMerge(
    /* Baked-in Defaults */
    ({
      maxFragmentCount:  0,          /* used to limit memory on receiver, 0=unlimited */
      maxFragmentSize:   3e5,        /* number of JS chars we want per message fragment */
      ackMode:           false,      /* don't ask remote to ack our transmissions */

      /* socketio v4 options below */
      maxHttpBufferSize: 1e9,        /* our buffer size - hard limit on message size */
      autoUnref:         dcpEnv.isReferencedTimerPlatform ? true : undefined,  /* socket.io + webpack 5 { node:global } work-around */
      forceNode:         dcpEnv.platform === 'nodejs', /* only used by socket.io < 4 */
      pingTimeout:       60e3 +  10, /* ms */
      pingInterval:      10e3 + 100, /* ms */
      transports:        dcpEnv.platform !== 'pythonmonkey' ? ['websocket', 'polling'] : ['polling'],
      perMessageDeflate: debugBuild,
      httpCompression:   debugBuild,
      allowUpgrades:     true,
      upgrade:           true,
      rememberUpgrade:   true,
      autoConnect:       true,
      cors: {
        origin: '*',
        methods: ['GET', 'POST']
      },
      extraHeaders: {
        'X-DCP': `${dcpEnv.platform} ${dcpBuild.config.build} ${dcpBuild.version}` /* leave footprints for debugging */
      },
      /* Ensure socketio does not reuse manager instances: we are in charge */
      forceNew:         true,
      multiplex:        false,
      reconnection:     false,
    }),
    options,
    /* Absolutely mandatory */
    ({
      path: this.url.pathname + 'dcp5/socketio',  // eslint-disable-line no-invalid-this
    }));

  /* short timeouts and debuggers don't get along well */
  if (usingDebugger && !process.env.DCP_NETWORK_TIMEOUTS_DEBUG)
  {
    options.pingTimeout        *= 10;
    options.pingInterval       *= 5;
  }

  return options;
}

/**
 * API - Factory which creates a new socketio listener, or throws. Emits 'listening' if the server
 * parameter is not provided and we create it, once it is ready for connections.
 *
 * @param {object}      config                  dcpConfig fragment - has .listen and .location
 * @param {object}      target                  the instance of Target to bind to
 * @param {object}      options                 [optional] A DCP config options variable (like dcpConfig.dcp.connectionOptions.default.socketio)
 * @constructor
 */
function SocketIOListener(config, target, options)
{
  this.on('error', () => {}); /* avoid "special" behaviour of NodeJS */

  this.url          = config.location;
  this.options      = this.buildOptions(options);
  this.connSequence = 0;
  this.seq          = ++listenerSeq;
  this.debugLabel   = `socketio(l:${this.seq}):`;
  this.isShutdown   = false;
  this.target       = target;

  if (dcpEnv.platform !== 'nodejs')
    throw new Error('target mode only supported in nodejs');

  debugging('socketio') && console.debug(this.debugLabel, `expecting requests to match regex ${this.url.pathname}\?EIO=[0-9]`);
  target.all({ path: this.options.path, search: { EIO: /^[0-9]/ }}, (request) => {
    /* Socket.io does this intrinsically because it plays with the server's events directly,
     * but we "handle" it explicitly here so that we don't trip the 404 handler.
     */
    debugging('socketio') && console.debug(this.debugLabel, 'Handling', request.url);
  });

  /* While we normally want to inherit options from parent to child socket via this.options,
   * we don't want this to happen for maxHttpBufferSize due to the way it is used and calculated
   * in the transport constructor. A buffer that is too small (eg false) will cause us to not
   * recognize socket.io SIDs.
   */
  this.options.maxHttpBufferSize = this.options.maxHttpBufferSize || 1e9;
  const socketOptions = this.options;
  this.socketServer = new ((options.module || requireNative('socket.io')).Server)(target.httpServer, socketOptions);
  this.socketServer.on('connection', (socket) => !this.isShutdown && this.handleConnectionEvent(config, socket));
  this.socketServer.on('error', (error) => {
    debugging() && console.error(this.debugLabel, 'socketio transport trapped listener error:', error);
    throw error;
  });
  debugging() && console.debug(this.debugLabel, 'Socketio listener initialized for path', this.options.path);
  debugging('socketio') || debugging('socketio-config') && dumpOptions(this.debugLabel, this.options);

  if (dcpEnv.platform === 'nodejs')
  {
    this[inspect] = () => `[Object SocketIOListener(${(this.isClosed && 'closed') || 'listening'}) `
      + `${this.url.pathname}]`;
  }

  /* Synthesize a per-transport listener event as Socket.io does not provide one. We assume that the
   * Socket.io server listens as soon as the underlying HTTP server listens.
   */
  if (target.httpServer?.listening)
    this.emit('listening', this);
  else
    target.on('listening', () => this.emit('listening', this));
}
SocketIOListener.prototype = Object.create(EventEmitter.prototype);
SocketIOListener.prototype.constructor = SocketIOListener;
SocketIOListener.prototype.buildOptions = buildOptions;

/** Stop listening for connections */
SocketIOListener.prototype.close = function SocketIOListener$$close()
{
  if (this.isClosed)
    return;
  this.isClosed = true;
  this.shutdown();
  this.emit('end');

  /* Calling socketServer.close() tears down all open client connections and the webSocket service. The
   * SocketIO DEBUG output suggests that this actually happens twice for some reason.
   */
  this.socketServer.close(() => {
    delete this.socketServer;
    this.emit('close');
    debugging('socketio') && console.debug(this.debugLabel, 'Closed socketio server');
  });
}

/** Stop accepting new connections */
SocketIOListener.prototype.shutdown = function SocketIOListener$$shutdown()
{
  if (this.isShutdown)
    return;
  debugging('socketio') && console.debug(this.debugLabel, 'listener shutdown');
  this.isShutdown = true;
  this.removeAllListeners('connection');
}

/** Remove any event loop references used by this transport instance */
SocketIOListener.prototype.unref = function SocketIOListener$$unref()
{
  void false;
}

/**
 * Handle the socketio connection event, which is fired upon a connection from client.
 * Used by Target->SocketIOListener to create transport instances.
 *
 * It is expected that code hooking the 'connection' event will hook the 'message' in
 * the same pass of the event loop as it is invoked; otherwise, messages may be missed.
 *
 * @param {object} socket     a new connection emitted by the socket.io server
 */
SocketIOListener.prototype.handleConnectionEvent = function SocketIOListener$$handleConnectionEvent(config, socket)
{
  var transport = new SocketIOTransport(config, this.options, socket);

  this.connSequence++;
  transport.debugLabel = `socketio(t:${this.seq}.${this.connSequence}):`

  /* Ensure we don't emit any message events until the application or Connection class
   * has had the chance to set up message handlers via the connection event.
   */
  transport.rxReady = false;
  this.emit('connection', transport);
  transport.rxReady = true;
  transport.emitReadyMessages();
}

function prettyPrintOptions(options)
{
  const keys = Object.keys(options);
  const mkl = keys.reduce((a,b) => a.length > b.length ? a : b).length; /* max key len */

  for (let key of keys)
  {
    let value = options[key];

    if (typeof value === 'number' && String(value).match(/000$/))
      value = chalk.yellow(value.toExponential());
    console.debug(' ', key.padEnd(mkl + 1, ' '), value);
  }
}

function dumpOptions(debugLabel, options)
{
  var soptions = Object.assign({}, options);
  delete soptions.maxFragmentCount;
  delete soptions.maxFragmentSize;
  delete soptions.ackMode;

  console.debug(debugLabel, 'socketio options:');
  prettyPrintOptions(soptions);

  const maxBytesPerFragment = options.maxFragmentSize * 3; /* UCS-2 to UTF-8 worst case */
  const maxBitsPerFragment = maxBytesPerFragment * 10; /* ~ 20% overhead */
  const bitsPerSec = maxBitsPerFragment / (options.pingTimeout / 1000);
  const mbps = bitsPerSec / 1e6;
  console.debug(debugLabel, 'minimum network speed for these options to work reliably:', Math.ceil(mbps * 20) / 20, 'Mbps');
}

/* Define API */
exports.TransportClass    = SocketIOTransport;
exports.Listener          = SocketIOListener;
